import { useEffect } from 'react';

export default function SuperbuySpreadsheetGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'The Complete Superbuy Spreadsheet Guide: How to Master Fashion Product Discovery',
      'description': 'Learn everything about the Superbuy Spreadsheet — a revolutionary fashion product discovery platform that organizes 14 curated categories of shoes, clothing, accessories and more into one easy-to-navigate resource.',
      'author': {
        '@type': 'Person',
        'name': 'Alex Carter',
        'jobTitle': 'Product Research Analyst'
      },
      'datePublished': '2026-07-13',
      'dateModified': '2026-07-13',
      'url': 'https://superbuyspread.ink/superbuy-spreadsheet-guide',
      'publisher': {
        '@type': 'Organization',
        'name': 'SuperBuySpread'
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': 'https://superbuyspread.ink/superbuy-spreadsheet-guide'
      }
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is the Superbuy Spreadsheet and who is it for?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Superbuy Spreadsheet is a comprehensive fashion product discovery resource designed for shoppers who want organized access to curated product collections. It serves fashion enthusiasts, collectors, stylists, and casual shoppers who prefer a structured approach to online browsing across 14 distinct categories.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How does the Superbuy Spreadsheet save time compared to manual browsing?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Instead of individually searching across multiple websites and platforms, the Superbuy Spreadsheet consolidates organized product links into one resource. Category-specific organization means users go directly to relevant collections, eliminating repetitive search workflows and reducing research time significantly.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What categories are available in the Superbuy Spreadsheet?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The spreadsheet covers Shoes, Hoodies and Sweaters, T-Shirts, Jackets, Pants and Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff — 14 categories in total.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How often is the Superbuy Spreadsheet content refreshed?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Superbuy Spreadsheet is updated on a weekly basis. New product links are added, outdated ones are removed, and categories are reorganized based on shopper feedback and emerging fashion trends.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Can beginners with no fashion experience use the Superbuy Spreadsheet?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Absolutely. The spreadsheet is organized with clear category labels and straightforward navigation. Beginners can start with broad categories like Shoes or T-Shirts and gradually explore more specific sections as they become comfortable with the platform.'
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <main>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="flex items-center justify-between px-6 md:px-12 py-4 max-w-7xl mx-auto w-full">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-primary-800 whitespace-nowrap">SuperBuySpread</span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Back to Home
            </a>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <section className="py-14 md:py-20 px-6 md:px-12 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-medium tracking-widest uppercase text-accent-600 bg-accent-100/60 px-3 py-1 rounded-full whitespace-nowrap">Complete Guide</span>
            <span className="text-xs text-foreground-400">Updated July 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            The Complete Superbuy Spreadsheet Guide: How to Master Fashion Product Discovery
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mt-4 leading-relaxed">
            Learn everything about the Superbuy Spreadsheet — a revolutionary approach to organized fashion shopping that consolidates 14 product categories into one structured resource.
          </p>
          <div className="flex items-center gap-4 mt-8 pt-6 border-t border-background-200/60">
            <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
              <span className="text-sm font-semibold text-secondary-700">AC</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Alex Carter</p>
              <p className="text-xs text-foreground-500">Product Research Analyst</p>
            </div>
            <span className="ml-auto text-xs text-foreground-400">7 min read</span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-12 md:py-16 px-6 md:px-12 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {/* Direct Answer Box */}
          <div className="bg-background-100 border border-background-200/60 rounded-2xl p-6 mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-secondary-600 mb-2">Quick Answer</p>
            <p className="text-sm font-semibold text-foreground-900 mb-1">What is Superbuy Spreadsheet?</p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Superbuy Spreadsheet is a structured collection of fashion product resources that helps shoppers discover and organize items efficiently across 14 curated categories — from premium sneakers to designer accessories. Unlike traditional online browsing that requires navigating multiple websites individually, the spreadsheet consolidates verified product links into one accessible directory, updated weekly.
            </p>
          </div>

          {/* Section 1 - Direct Answer to Search Intent */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            What Is the Superbuy Spreadsheet and Why It Matters
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            In the sprawling landscape of online fashion shopping, discovery remains the single hardest challenge. Every day, millions of shoppers scroll through endless product pages, comparison sites, and social media feeds hoping to stumble upon pieces that match their taste and budget. The Superbuy Spreadsheet addresses this problem head-on by replacing chaos with clarity.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            At its core, the Superbuy Spreadsheet functions as a carefully organized directory spanning <strong>14 fashion categories</strong>. Each category contains direct links to curated product collections, organized in a way that makes sense for real shopping behavior. Instead of searching &quot;sneakers&quot; across five different platforms and trying to compare results, users open the Shoes category and immediately access a pre-organized list of relevant products.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            The platform was built around a simple observation: most online shoppers spend more time searching than actually shopping. By front-loading the organization work, the Superbuy Spreadsheet lets users redirect that time toward making informed purchasing decisions.
          </p>

          {/* Section 2 - Experience Layer */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            How Real Shoppers Use the Superbuy Spreadsheet
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            To understand why this resource has gained traction, it helps to look at how different types of shoppers incorporate it into their routines.
          </p>

          <div className="bg-background-100 rounded-2xl p-6 mb-6 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
              <i className="ri-user-star-line text-accent-500 mr-2"></i>
              The Fashion Collector
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Collectors typically need access to multiple categories simultaneously — a pair of limited-release sneakers in Shoes, a matching hoodie from Hoodies/Sweaters, and perhaps a coordinated cap from Headwear. Without the spreadsheet, this would mean juggling dozens of browser tabs. With it, each category is a single click away. The weekly update cycle is particularly valuable for collectors tracking new drops, as the spreadsheet refreshes with new product links before most social feeds catch up.
            </p>
          </div>

          <div className="bg-background-100 rounded-2xl p-6 mb-6 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
              <i className="ri-shopping-bag-3-line text-accent-500 mr-2"></i>
              The Casual Browser
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Casual browsers benefit from reduced decision fatigue. Rather than facing an overwhelming homepage full of algorithmic recommendations, the spreadsheet shows clear, stable categories. A user looking for a new jacket knows exactly where to click — the Jackets category. This predictability builds shopping confidence, especially for people who find traditional e-commerce interfaces intimidating or exhausting.
            </p>
          </div>

          <div className="bg-background-100 rounded-2xl p-6 mb-6 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
              <i className="ri-brush-line text-accent-500 mr-2"></i>
              The Personal Stylist
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Professional stylists use the Superbuy Spreadsheet as a client-sourcing tool. When a client requests specific combinations — say, formal shoes with a matching belt and bag — the stylist can quickly pull options from Shoes, Belts, and Bags without starting from scratch each time. The organized structure means less time researching and more time curating personalized recommendations.
            </p>
          </div>

          {/* Comparison Table */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Superbuy Spreadsheet vs Traditional Shopping Methods
          </h2>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Feature</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Superbuy Spreadsheet</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Manual Search</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Organization</td>
                  <td className="py-3 px-4 text-secondary-700">14 structured categories</td>
                  <td className="py-3 px-4 text-foreground-500">Unstructured, ad-hoc</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Research Time</td>
                  <td className="py-3 px-4 text-secondary-700">Minutes</td>
                  <td className="py-3 px-4 text-foreground-500">Hours per session</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Update Frequency</td>
                  <td className="py-3 px-4 text-secondary-700">Weekly</td>
                  <td className="py-3 px-4 text-foreground-500">Continuous but scattered</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Discoverability</td>
                  <td className="py-3 px-4 text-secondary-700">High — unified directory</td>
                  <td className="py-3 px-4 text-foreground-500">Low — fragmented platforms</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-foreground-700 font-medium">Beginner Friendly</td>
                  <td className="py-3 px-4 text-secondary-700">Yes — labeled categories</td>
                  <td className="py-3 px-4 text-foreground-500">Depends on experience</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Decision Layer */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Who Should Use the Superbuy Spreadsheet
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-secondary-50 border border-secondary-200/60 rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold text-secondary-800 mb-3">
                <i className="ri-check-line text-secondary-500 mr-2"></i>
                Best For
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Fashion beginners who need guidance navigating product categories</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Product researchers comparing items across multiple categories</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Budget-conscious shoppers seeking organized browsing</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Collectors tracking new drops across multiple fashion segments</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-secondary-500 mt-0.5">&#10003;</span>
                  <span>Stylists sourcing options for client wardrobes</span>
                </li>
              </ul>
            </div>
            <div className="bg-accent-50 border border-accent-200/60 rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold text-accent-700 mb-3">
                <i className="ri-information-line text-accent-500 mr-2"></i>
                Not Ideal For
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-400 mt-0.5">&#10007;</span>
                  <span>Users who prefer completely unstructured, random browsing</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-400 mt-0.5">&#10007;</span>
                  <span>Shoppers who exclusively use a single platform and never explore</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="text-accent-400 mt-0.5">&#10007;</span>
                  <span>Those expecting real-time inventory tracking (the spreadsheet links to listings)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Getting Started */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Getting Started: A Step-by-Step Approach
          </h2>
          <div className="space-y-5 mb-8">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">1</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Start with your primary interest</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">If you are looking for sneakers, begin with the Shoes category. If outerwear is your priority, open Jackets. Do not try to explore all 14 categories at once — focus on one, get comfortable, then expand.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">2</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Browse with intent</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Each category link opens directly to an organized product collection on the main website. Take your time browsing. Bookmark items that catch your eye for later comparison.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">3</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Cross-reference categories</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Once comfortable with individual categories, start cross-referencing. Found shoes you like? Check Belts and Bags for matching accessories. This is where the spreadsheet truly shines as a complete styling tool.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">4</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Return weekly for updates</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">The Superbuy Spreadsheet refreshes every week. New product links appear, collections expand, and categories evolve. Regular visitors consistently discover pieces before they trend.</p>
              </div>
            </div>
          </div>

          {/* The 14 Categories Overview */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            A Tour of the 14 Superbuy Spreadsheet Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            Each category serves a distinct shopping purpose, but they are designed to work together as a unified discovery system. Here is a quick overview of every category and what to expect.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {[
              { name: 'Shoes', desc: 'The anchor category featuring sneakers, formal shoes, boots, and seasonal footwear. Typically the most frequently updated category.' },
              { name: 'Hoodies/Sweaters', desc: 'Comfort-focused category spanning lightweight pullovers to heavyweight winter hoodies in various styles and colorways.' },
              { name: 'T-Shirts', desc: 'Essential basics category with graphic tees, plain cotton shirts, and designer t-shirts across price ranges.' },
              { name: 'Jackets', desc: 'Outerwear destination covering bombers, denim jackets, parkas, and technical outerwear for all seasons.' },
              { name: 'Pants/Shorts', desc: 'Bottom-wear category including jeans, cargo pants, tailored trousers, and seasonal shorts.' },
              { name: 'Headwear', desc: 'Caps, beanies, bucket hats, and accessories that complete any outfit from the top down.' },
              { name: 'Sets', desc: 'Coordinated outfit sets that eliminate the guesswork of matching individual pieces.' },
              { name: 'Underwear/Underpants', desc: 'Foundation category with everyday essentials and premium undergarment options.' },
              { name: 'Jersey', desc: 'Sports and athleisure jerseys from classic designs to limited-edition releases.' },
              { name: 'Accessories', desc: 'Jewelry, watches, sunglasses, scarves, and the finishing touches that elevate any look.' },
              { name: 'Bags', desc: 'Backpacks, totes, crossbody bags, and luggage spanning functional to luxury designs.' },
              { name: 'Belts', desc: 'Leather belts, canvas belts, and statement pieces that define waistlines.' },
              { name: 'Perfumes', desc: 'Fragrance discovery category featuring designer and niche scents for both men and women.' },
              { name: 'Other Stuff', desc: 'The catch-all for unique finds that do not fit neatly into other categories — often the most interesting section.' },
            ].map((cat) => (
              <div key={cat.name} className="bg-background-100 rounded-xl p-4 border border-background-200/60">
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">{cat.name}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 mb-8">
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: How does the Superbuy Spreadsheet stay current?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">The spreadsheet undergoes a weekly refresh cycle. Product links are verified, outdated entries are pruned, and new collections are integrated based on community feedback and emerging trends. This regular maintenance ensures users always access relevant, active product listings.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Can I request a new category be added?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">As the platform evolves, category expansion is always under consideration. The current 14 categories cover the vast majority of fashion shopping needs, but the &quot;Other Stuff&quot; category serves as a flexible space for items that span boundaries while discussions about new dedicated categories continue.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: What makes the Superbuy Spreadsheet different from a regular bookmark folder?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">A bookmark folder is static — links go stale, organization becomes outdated, and there is no curation. The Superbuy Spreadsheet is actively maintained with weekly updates, community-driven category refinement, and a structure designed specifically for fashion discovery workflows rather than generic link storage.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Is there a mobile-friendly way to access the spreadsheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Yes. The entire Superbuy Spreadsheet platform is designed responsively, so categories and links are fully accessible on smartphones and tablets. The mobile experience preserves the same organized structure with touch-friendly navigation throughout.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: How do I stay updated on changes to the Superbuy Spreadsheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">The best way is to bookmark the <a href="/" className="text-secondary-600 hover:text-secondary-700 underline">Superbuy Spreadsheet homepage</a> and check back weekly. You can also subscribe to the newsletter through the form in the website footer to receive update notifications and curated category highlights directly in your inbox.</p>
            </div>
          </div>

          {/* Navigation back */}
          <div className="mt-14 pt-8 border-t border-background-200/60">
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground-400">Continue Reading</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <a href="/superbuy-spreadsheet-shoes-guide" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-footprint-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Shoes Guide</p>
                    <p className="text-xs text-foreground-500">Deep dive into footwear</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/comparison" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-scales-3-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Comparison</p>
                    <p className="text-xs text-foreground-500">Spreadsheet vs alternatives</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/is-superbuy-spreadsheet-safe" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-shield-check-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Safety Guide</p>
                    <p className="text-xs text-foreground-500">Trust & verification</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <a
                  href="/"
                  className="group flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
                  <span className="whitespace-nowrap">Back to Superbuy Spreadsheet Home</span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Go to Superbuy Sheet</span>
                  <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Footer minimal */}
      <footer className="bg-secondary-900 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <a href="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-xs"></i>
              </div>
              <span className="font-heading text-sm font-semibold text-background-50 whitespace-nowrap">SuperBuySpread</span>
            </a>
            <p className="text-xs text-secondary-500">
              &copy; 2026 SuperBuySpread. <a href="/" className="hover:text-secondary-300 transition-colors">Home</a> &bull; <a href="/superbuy-spreadsheet-guide" className="hover:text-secondary-300 transition-colors">Complete Guide</a>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}